/**
 * StudioMap · «Карта студии» — orientation beat of the onboarding.
 * slot_onboarding_redesign OB.3 (23 MAY 2026).
 *
 * Shown once, right after the Welcome screen, on the first Dashboard load.
 * A static "where everything lives" cheat-sheet — NOT a step-through tour.
 * Six surface tiles answer «что есть на курсе, куда идти, что там искать»
 * (Genie OB.2 revision). Dismissible: primary CTA, ghost link, ESC, or
 * backdrop click — all call `onDismiss`.
 */

import { useEffect } from 'react';
import { Buddy } from '@/components/Buddy';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface StudioMapProps {
  /** Mark the Studio Map as dismissed (onboarding-local state). */
  onDismiss: () => void;
  /**
   * Primary-CTA follow-up (Genie 7 JUN 2026): «Понятно, к делу →» дополнительно
   * уводит на Доску, где чеклист «Первые шаги» продолжает вести. «Скрыть»,
   * ESC и клик по фону — только dismiss, без навигации.
   */
  onGo?: () => void;
}

interface SurfaceTile {
  name: string;
  desc: string;
}

/** Six navigable surfaces — order mirrors the Sidebar (main items, then foot).
 *  Эмодзи-иконки зачищены 7 JUN 2026 (Genie · `no_emoji_no_ai_patterns`). */
const TILES: ReadonlyArray<SurfaceTile> = [
  { name: 'Сегодня', desc: 'Дашборд: где остановилась, что в работе' },
  { name: 'Карта', desc: 'Весь маршрут курса по спринтам и блокам' },
  { name: 'Доска', desc: 'Тикеты спринта в колонках — есть и вид списком' },
  { name: 'Глоссарий', desc: 'Все термины QA, вызов по ⌘K откуда угодно' },
  { name: 'Обзор', desc: 'Дашборд курса: спринт, ритм, ISTQB, умения, capstone' },
  { name: 'Профиль', desc: 'Имя, настройки, перепройти знакомство' },
];

export function StudioMap({ onDismiss, onGo }: StudioMapProps) {
  const cardRef = useFocusTrap<HTMLDivElement>(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="onb-map-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        ref={cardRef}
        className="onb-card onb-map"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onb-map-title"
      >
        <div className="onb-map-head">
          <span className="onb-buddy" aria-hidden="true">
            <Buddy size={26} />
          </span>
          <h2 id="onb-map-title" className="onb-title">
            Карта <span className="onb-accent">студии</span>
          </h2>
        </div>
        <p className="onb-map-lead">
          Первый тикет закрыт — теперь осмотримся. Шесть мест — вот где что лежит.
          Блок 1 (спринты S01–S06) уже открыт, дальше — по мере прохождения.
          Путь: Trainee → Junior QA → 🎓 ISTQB CTFL.
        </p>
        <div className="onb-map-grid">
          {TILES.map((t) => (
            <div key={t.name} className="onb-map-tile">
              <div className="onb-tile-name">{t.name}</div>
              <div className="onb-tile-desc">{t.desc}</div>
            </div>
          ))}
        </div>
        <div className="onb-map-actions">
          <button
            type="button"
            className="onb-btn onb-btn-primary"
            onClick={() => {
              onDismiss();
              onGo?.();
            }}
          >
            Понятно, к делу →
          </button>
          <button type="button" className="onb-link" onClick={onDismiss}>
            Скрыть
          </button>
        </div>
      </div>
    </div>
  );
}
